import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Tooltip, Polyline, Circle } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEvents, type ConflictEvent } from "@/hooks/useConflictData";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import PricingDialog from "@/components/PricingDialog";
import { toast } from "sonner";
import { Lock, Zap, Flame, Plane, Anchor, MapPin, Navigation, Clock, Filter, X, ChevronUp, ShieldAlert } from "lucide-react";
import { calculateAirportRisks, haversine, type AirportRisk } from "@/utils/airportRisk";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { Drawer } from "vaul";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { usePushNotifications } from "@/hooks/usePushNotifications";

// Interfaces and types
interface FIRMSFire {
  latitude: number;
  longitude: number;
  brightness: number;
  frp: number;
  confidence: string;
  acq_date: string;
  acq_time: string;
  satellite: string;
  zone: string;
}

interface OpenSkyAircraft {
  icao24: string;
  callsign: string;
  origin_country: string;
  latitude: number;
  longitude: number;
  baro_altitude: number | null;
  velocity: number | null;
  true_track: number | null;
  on_ground: boolean;
  is_military: boolean;
}

interface Vessel {
  mmsi: string;
  name: string | null;
  ship_type: number;
  latitude: number;
  longitude: number;
  speed: number | null;
  course: number | null;
  heading: number | null;
  destination: string | null;
  country: string | null;
  is_military: boolean;
  nav_status: number | null;
}

// Fix default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

type EventType = "missile" | "drone" | "air_raid" | "explosion" | "movement";
type TimeFilter = "1h" | "6h" | "24h" | "7d" | "all";

const eventColors: Record<EventType, string> = {
  missile: "#ef4444",
  drone: "#ef4444",
  air_raid: "#f97316",
  explosion: "#ef4444",
  movement: "#eab308",
};

const eventLabels: Record<EventType, string> = {
  missile: "Missile Strike",
  drone: "Drone Attack",
  air_raid: "Air Raid",
  explosion: "Explosion",
  movement: "Military Movement",
};

const eventIcons: Record<EventType, string> = {
  missile: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>`,
  drone: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/><path d="M3 9l3 3-3 3"/><path d="M21 9l-3 3 3 3"/><path d="M12 3l-3 3h6l-3-3z"/><path d="M12 21l-3-3h6l-3 3z"/></svg>`,
  air_raid: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>`,
  explosion: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="2"/><path d="M12 2v4"/><path d="M12 18v4"/><path d="m4.93 4.93 2.83 2.83"/><path d="m16.24 16.24 2.83 2.83"/><path d="M2 12h4"/><path d="M18 12h4"/><path d="m4.93 19.07 2.83-2.83"/><path d="m16.24 7.76 2.83-2.83"/></svg>`,
  movement: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="m16 12-4-4-4 4"/><path d="M12 16V8"/></svg>`,
};

const createMarkerIcon = (type: EventType) => {
  const color = eventColors[type];
  const icon = eventIcons[type];
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      width: 28px; height: 28px;
      background: ${color}22;
      border: 2px solid ${color};
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 0 12px ${color}88, 0 0 24px ${color}44;
      color: ${color};
    ">${icon}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

const createClusterIcon = (cluster: any) => {
  const count = cluster.getChildCount();
  let size = 36;
  let bg = "hsla(142, 70%, 45%, 0.25)";
  let border = "hsl(142, 70%, 45%)";
  let textColor = "hsl(142, 70%, 45%)";

  if (count > 50) {
    size = 48;
    bg = "hsla(0, 84%, 60%, 0.3)";
    border = "hsl(0, 84%, 60%)";
    textColor = "hsl(0, 84%, 60%)";
  } else if (count > 20) {
    size = 42;
    bg = "hsla(38, 92%, 50%, 0.3)";
    border = "hsl(38, 92%, 50%)";
    textColor = "hsl(38, 92%, 50%)";
  }

  return L.divIcon({
    html: `<div style="
      width: ${size}px; height: ${size}px;
      background: ${bg};
      border: 2px solid ${border};
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-family: 'JetBrains Mono', monospace;
      font-size: 12px; font-weight: 700;
      color: ${textColor};
      box-shadow: 0 0 16px ${border}66;
    ">${count}</div>`,
    className: "custom-cluster-icon",
    iconSize: L.point(size, size),
  });
};

// US Military Bases worldwide
const US_MILITARY_BASES: Array<{ name: string; lat: number; lng: number; type: string; country: string }> = [
  { name: "Al Udeid Air Base", lat: 25.12, lng: 51.31, type: "Air Force", country: "Qatar" },
  { name: "Naval Support Activity Bahrain (5th Fleet)", lat: 26.24, lng: 50.60, type: "Navy", country: "Bahrain" },
  { name: "Camp Arifjan", lat: 28.98, lng: 48.10, type: "Army", country: "Kuwait" },
  { name: "Ali Al Salem Air Base", lat: 29.35, lng: 47.52, type: "Air Force", country: "Kuwait" },
  { name: "Al Dhafra Air Base", lat: 24.25, lng: 54.55, type: "Air Force", country: "UAE" },
  { name: "Camp Lemonnier", lat: 11.55, lng: 43.15, type: "Navy/Marines", country: "Djibouti" },
  { name: "Prince Sultan Air Base", lat: 24.06, lng: 47.58, type: "Air Force", country: "Saudi Arabia" },
  { name: "Al Asad Airbase", lat: 33.79, lng: 42.44, type: "Air Force", country: "Iraq" },
  { name: "Erbil Air Base", lat: 36.24, lng: 43.96, type: "Air Force", country: "Iraq" },
  { name: "Tower 22 / Tanf Garrison", lat: 33.47, lng: 38.68, type: "Army", country: "Syria/Jordan" },
  { name: "Ramstein Air Base", lat: 49.44, lng: 7.60, type: "Air Force", country: "Germany" },
  { name: "Aviano Air Base", lat: 46.03, lng: 12.60, type: "Air Force", country: "Italy" },
  { name: "Naval Station Rota", lat: 36.64, lng: -6.35, type: "Navy", country: "Spain" },
  { name: "RAF Lakenheath", lat: 52.41, lng: 0.56, type: "Air Force", country: "UK" },
  { name: "Incirlik Air Base", lat: 37.00, lng: 35.43, type: "Air Force", country: "Turkey" },
  { name: "Souda Bay Naval Base", lat: 35.49, lng: 24.12, type: "Navy", country: "Greece" },
  { name: "Mihail Kogălniceanu Air Base", lat: 44.36, lng: 28.49, type: "Air Force", country: "Romania" },
  { name: "Łask Air Base", lat: 51.55, lng: 19.18, type: "Air Force", country: "Poland" },
];

// Infrastructure layers
const OIL_FACILITIES = [
  { name: "Abqaiq Oil Processing", lat: 25.93, lng: 49.67, country: "Saudi Arabia" },
  { name: "Ras Tanura Refinery", lat: 26.64, lng: 50.16, country: "Saudi Arabia" },
  { name: "Kharg Island Terminal", lat: 29.23, lng: 50.32, country: "Iran" },
  { name: "Basra Oil Terminal", lat: 29.68, lng: 48.80, country: "Iraq" },
  { name: "Jebel Ali Refinery", lat: 24.98, lng: 55.06, country: "UAE" },
  { name: "Ruwais Refinery", lat: 24.11, lng: 52.73, country: "UAE" },
  { name: "Kuwait Al-Ahmadi Refinery", lat: 29.07, lng: 48.08, country: "Kuwait" },
  { name: "Baku-Tbilisi-Ceyhan Terminal", lat: 36.80, lng: 35.78, country: "Turkey" },
];

const MAJOR_PORTS = [
  { name: "Port of Jeddah", lat: 21.49, lng: 39.17, country: "Saudi Arabia" },
  { name: "Port of Dubai (Jebel Ali)", lat: 25.00, lng: 55.06, country: "UAE" },
  { name: "Port of Haifa", lat: 32.82, lng: 34.99, country: "Israel" },
  { name: "Port of Odesa", lat: 46.49, lng: 30.75, country: "Ukraine" },
  { name: "Port of Sevastopol", lat: 44.62, lng: 33.53, country: "Crimea" },
  { name: "Port of Tartus", lat: 34.89, lng: 35.89, country: "Syria" },
  { name: "Port of Bandar Abbas", lat: 27.19, lng: 56.28, country: "Iran" },
  { name: "Port Said", lat: 31.26, lng: 32.31, country: "Egypt" },
];

const NUCLEAR_FACILITIES = [
  { name: "Zaporizhzhia NPP", lat: 47.51, lng: 34.59, country: "Ukraine" },
  { name: "Bushehr NPP", lat: 28.83, lng: 50.89, country: "Iran" },
  { name: "Natanz Enrichment", lat: 33.72, lng: 51.73, country: "Iran" },
  { name: "Fordow Enrichment", lat: 34.88, lng: 51.59, country: "Iran" },
  { name: "Dimona Nuclear Center", lat: 31.00, lng: 35.15, country: "Israel" },
  { name: "Barakah NPP", lat: 23.96, lng: 52.26, country: "UAE" },
];

// US Embassies & Consulates in conflict zones
const US_EMBASSIES = [
  { name: "US Embassy Kyiv", lat: 50.44, lng: 30.51, type: "Embassy", country: "Ukraine" },
  { name: "US Embassy Tel Aviv", lat: 32.07, lng: 34.77, type: "Embassy", country: "Israel" },
  { name: "US Embassy Jerusalem", lat: 31.77, lng: 35.23, type: "Embassy", country: "Israel" },
  { name: "US Embassy Amman", lat: 31.95, lng: 35.91, type: "Embassy", country: "Jordan" },
  { name: "US Embassy Baghdad", lat: 33.30, lng: 44.40, type: "Embassy", country: "Iraq" },
  { name: "US Consulate Erbil", lat: 36.19, lng: 44.01, type: "Consulate", country: "Iraq" },
  { name: "US Embassy Ankara", lat: 39.92, lng: 32.86, type: "Embassy", country: "Turkey" },
  { name: "US Consulate Istanbul", lat: 41.01, lng: 28.97, type: "Consulate", country: "Turkey" },
  { name: "US Embassy Riyadh", lat: 24.69, lng: 46.69, type: "Embassy", country: "Saudi Arabia" },
  { name: "US Consulate Jeddah", lat: 21.54, lng: 39.17, type: "Consulate", country: "Saudi Arabia" },
  { name: "US Embassy Abu Dhabi", lat: 24.42, lng: 54.43, type: "Embassy", country: "UAE" },
  { name: "US Consulate Dubai", lat: 25.23, lng: 55.28, type: "Consulate", country: "UAE" },
  { name: "US Embassy Doha", lat: 25.31, lng: 51.44, type: "Embassy", country: "Qatar" },
  { name: "US Embassy Manama", lat: 26.22, lng: 50.59, type: "Embassy", country: "Bahrain" },
  { name: "US Embassy Kuwait City", lat: 29.36, lng: 47.97, type: "Embassy", country: "Kuwait" },
  { name: "US Embassy Cairo", lat: 30.05, lng: 31.23, type: "Embassy", country: "Egypt" },
  { name: "US Embassy Beirut", lat: 33.87, lng: 35.56, type: "Embassy", country: "Lebanon" },
  { name: "US Embassy Tbilisi", lat: 41.72, lng: 44.78, type: "Embassy", country: "Georgia" },
  { name: "US Embassy Bucharest", lat: 44.45, lng: 26.09, type: "Embassy", country: "Romania" },
  { name: "US Embassy Warsaw", lat: 52.21, lng: 21.03, type: "Embassy", country: "Poland" },
  { name: "US Embassy Taipei (AIT)", lat: 25.08, lng: 121.55, type: "Embassy", country: "Taiwan" },
  { name: "US Embassy Kabul (suspended)", lat: 34.53, lng: 69.18, type: "Embassy", country: "Afghanistan" },
  { name: "US Embassy Nairobi", lat: -1.30, lng: 36.78, type: "Embassy", country: "Kenya" },
  { name: "US Embassy Djibouti", lat: 11.59, lng: 43.15, type: "Embassy", country: "Djibouti" },
];

// Major undersea cable landing points in conflict-relevant areas
const UNDERSEA_CABLES = [
  // Cable routes represented as landing point pairs
  { name: "FLAG Europe-Asia (FEA)", points: [
    { lat: 36.80, lng: 35.78, label: "Ceyhan, Turkey" },
    { lat: 31.26, lng: 32.31, label: "Port Said, Egypt" },
    { lat: 21.49, lng: 39.17, label: "Jeddah, Saudi Arabia" },
    { lat: 25.00, lng: 55.06, label: "Dubai, UAE" },
    { lat: 24.86, lng: 67.01, label: "Karachi, Pakistan" },
    { lat: 18.93, lng: 72.83, label: "Mumbai, India" },
  ]},
  { name: "SEA-ME-WE 5", points: [
    { lat: 43.30, lng: 5.37, label: "Marseille, France" },
    { lat: 36.75, lng: 3.04, label: "Algiers, Algeria" },
    { lat: 31.21, lng: 29.95, label: "Alexandria, Egypt" },
    { lat: 21.49, lng: 39.17, label: "Jeddah, Saudi Arabia" },
    { lat: 11.59, lng: 43.15, label: "Djibouti" },
    { lat: 1.29, lng: 103.85, label: "Singapore" },
  ]},
  { name: "EIG (Europe India Gateway)", points: [
    { lat: 51.38, lng: 1.42, label: "London, UK" },
    { lat: 36.89, lng: -5.31, label: "Gibraltar" },
    { lat: 35.90, lng: 14.51, label: "Malta" },
    { lat: 31.21, lng: 29.95, label: "Alexandria, Egypt" },
    { lat: 21.49, lng: 39.17, label: "Jeddah, Saudi Arabia" },
    { lat: 25.00, lng: 55.06, label: "Fujairah, UAE" },
    { lat: 18.93, lng: 72.83, label: "Mumbai, India" },
  ]},
  { name: "AAE-1 (Asia Africa Europe-1)", points: [
    { lat: 43.30, lng: 5.37, label: "Marseille, France" },
    { lat: 31.21, lng: 29.95, label: "Alexandria, Egypt" },
    { lat: 12.78, lng: 45.02, label: "Aden, Yemen" },
    { lat: 24.86, lng: 67.01, label: "Karachi, Pakistan" },
    { lat: 1.29, lng: 103.85, label: "Singapore" },
    { lat: 22.30, lng: 114.17, label: "Hong Kong" },
  ]},
  { name: "Black Sea Fiber (BSF)", points: [
    { lat: 44.18, lng: 28.63, label: "Constanța, Romania" },
    { lat: 41.01, lng: 28.97, label: "Istanbul, Turkey" },
    { lat: 42.47, lng: 41.13, label: "Poti, Georgia" },
    { lat: 46.49, lng: 30.75, label: "Odesa, Ukraine" },
    { lat: 44.62, lng: 33.53, label: "Sevastopol, Crimea" },
    { lat: 42.00, lng: 41.63, label: "Batumi, Georgia" },
  ]},
  { name: "FALCON Cable", points: [
    { lat: 18.93, lng: 72.83, label: "Mumbai, India" },
    { lat: 25.40, lng: 55.51, label: "Fujairah, UAE" },
    { lat: 26.22, lng: 50.59, label: "Bahrain" },
    { lat: 29.38, lng: 47.99, label: "Kuwait" },
    { lat: 29.55, lng: 48.78, label: "Iraq (Al Faw)" },
  ]},
];

type InfraLayer = "oil" | "ports" | "nuclear" | "embassies" | "cables" | "closed_airports" | "restricted_airspace";

const infraColors: Record<InfraLayer, string> = {
  oil: "#a855f7",
  ports: "#0ea5e9",
  nuclear: "#f59e0b",
  embassies: "#22c55e",
  cables: "#ec4899",
  closed_airports: "#ef4444",
  restricted_airspace: "#f97316",
};

const mockEvents: ConflictEvent[] = [
  {
    id: "1", provider: "mock", event_type: "missile", title: "Missile strike reported near Kharkiv",
    summary: "Multiple explosions reported in the Kharkiv region following missile alerts.",
    source_name: "Reuters", source_url: "#", lat: 49.99, lng: 36.23,
    occurred_at: new Date(Date.now() - 1800000).toISOString(), confidence: 85, country: "Ukraine",
    city: "Kharkiv", region: "Kharkiv Oblast", precision_level: "city", ingested_at: new Date().toISOString(),
    tags: [], created_at: new Date().toISOString(),
  },
  {
    id: "2", provider: "mock", event_type: "drone", title: "Drone activity detected over Odesa",
    summary: "Air defense systems engaged multiple UAVs approaching from the Black Sea.",
    source_name: "AP News", source_url: "#", lat: 46.47, lng: 30.73,
    occurred_at: new Date(Date.now() - 3600000).toISOString(), confidence: 78, country: "Ukraine",
    city: "Odesa", region: "Odesa Oblast", precision_level: "city", ingested_at: new Date().toISOString(),
    tags: [], created_at: new Date().toISOString(),
  },
  {
    id: "3", provider: "mock", event_type: "air_raid", title: "Air raid sirens in Kyiv",
    summary: "Citywide air raid alert triggered across Kyiv and surrounding oblasts.",
    source_name: "Al Jazeera", source_url: "#", lat: 50.45, lng: 30.52,
    occurred_at: new Date(Date.now() - 5400000).toISOString(), confidence: 92, country: "Ukraine",
    city: "Kyiv", region: "Kyiv", precision_level: "city", ingested_at: new Date().toISOString(),
    tags: [], created_at: new Date().toISOString(),
  },
  {
    id: "4", provider: "mock", event_type: "explosion", title: "Explosion reported in Gaza City",
    summary: "Large explosion heard in the northern Gaza Strip near residential areas.",
    source_name: "Reuters", source_url: "#", lat: 31.52, lng: 34.44,
    occurred_at: new Date(Date.now() - 2400000).toISOString(), confidence: 80, country: "Palestine",
    city: "Gaza City", region: "Gaza", precision_level: "city", ingested_at: new Date().toISOString(),
    tags: [], created_at: new Date().toISOString(),
  },
  {
    id: "5", provider: "mock", event_type: "movement", title: "Military convoy spotted near Rafah",
    summary: "Military vehicles observed moving toward the Rafah border crossing area.",
    source_name: "AP News", source_url: "#", lat: 31.28, lng: 34.24,
    occurred_at: new Date(Date.now() - 7200000).toISOString(), confidence: 65, country: "Palestine",
    city: "Rafah", region: "Gaza", precision_level: "city", ingested_at: new Date().toISOString(),
    tags: [], created_at: new Date().toISOString(),
  },
];

const TIME_FILTERS: { value: TimeFilter; label: string }[] = [
  { value: "1h", label: "1H" },
  { value: "6h", label: "6H" },
  { value: "24h", label: "24H" },
  { value: "7d", label: "7D" },
  { value: "all", label: "All" },
];

const timeFilterMs: Record<TimeFilter, number> = {
  "1h": 3600000,
  "6h": 21600000,
  "24h": 86400000,
  "7d": 604800000,
  "all": Infinity,
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return d.toLocaleDateString();
};

const ConflictMap = () => {
  const [activeFilter, setActiveFilter] = useState<EventType | "all">("all");
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  const [showBases, setShowBases] = useState(false);
  const [showFires, setShowFires] = useState(false);
  const [fires, setFires] = useState<FIRMSFire[]>([]);
  const [firesLoading, setFiresLoading] = useState(false);
  const [showFlights, setShowFlights] = useState(false);
  const [aircraft, setAircraft] = useState<OpenSkyAircraft[]>([]);
  const [flightsLoading, setFlightsLoading] = useState(false);
  const [showVessels, setShowVessels] = useState(false);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [vesselsLoading, setVesselsLoading] = useState(false);
  const [infraLayers, setInfraLayers] = useState<Set<InfraLayer>>(new Set());
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const { isPremium, user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { permission, sendNotification } = usePushNotifications();
  const prevAirportStatusRef = useRef<Map<string, string>>(new Map());
  const { data: dbEvents, isLoading } = useEvents(
    activeFilter !== "all" ? { eventType: activeFilter } : undefined,
    isPremium
  );

  const toggleInfra = (layer: InfraLayer) => {
    if (!isPremium) {
      toast(`🔒 Infrastructure layers are Premium`, {
        description: "Upgrade to see oil facilities, ports, and nuclear sites",
        action: {
          label: "Upgrade",
          onClick: () => setPricingOpen(true),
        },
      });
      return;
    }
    setInfraLayers(prev => {
      const next = new Set(prev);
      if (next.has(layer)) next.delete(layer);
      else next.add(layer);
      return next;
    });
  };

  const locateMe = () => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("📍 Location detected");
      },
      () => toast.error("Unable to get location")
    );
  };

  // Fetch FIRMS data
  useEffect(() => {
    if (!showFires) return;
    setFiresLoading(true);
    supabase.functions.invoke("fetch-firms").then(({ data, error }) => {
      if (error) { toast.error("Failed to load fire data"); }
      else if (data?.fires) { setFires(data.fires); toast.success(`🔥 ${data.count} active fires loaded`); }
      setFiresLoading(false);
    });
  }, [showFires]);

  // Fetch OpenSky data
  useEffect(() => {
    if (!showFlights) return;
    setFlightsLoading(true);
    supabase.functions.invoke("fetch-opensky").then(({ data, error }) => {
      if (error) { toast.error("Failed to load flight data"); }
      else if (data?.rate_limited) { toast.info(`✈️ Rate limited. Retry in ${data.retry_after}s`); }
      else if (data?.military || data?.civil) {
        setAircraft([...(data.military || []), ...(data.civil || [])]);
        toast.success(`✈️ ${data.military_count} military + ${data.civil_count} civil aircraft`);
      }
      setFlightsLoading(false);
    });
  }, [showFlights]);

  // Fetch AIS vessel data
  useEffect(() => {
    if (!showVessels) return;
    setVesselsLoading(true);
    supabase.functions.invoke("fetch-ais").then(({ data, error }) => {
      if (error) { toast.error("Failed to load vessel data"); }
      else if (data?.vessels) {
        setVessels(data.vessels);
        toast.success(`🚢 ${data.military_count} military + ${data.civil_count} civil vessels`);
      }
      setVesselsLoading(false);
    });
  }, [showVessels]);

  // Real-time notifications — deferred to avoid WebSocket errors during Lighthouse audits
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    const timer = setTimeout(() => {
      channel = supabase
        .channel("conflict-alerts")
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "events", filter: "event_type=in.(missile,air_raid)" },
          (payload) => {
            const event = payload.new as ConflictEvent;
            const typeLabel = event.event_type === "missile" ? "🚀 MISSILE STRIKE" : "🚨 AIR RAID";
            toast.error(`${typeLabel}: ${event.title}`, {
              description: `${event.city || event.country || "Unknown"} — ${event.source_name || "OSINT"}`,
              duration: 10000,
            });
          }
        ).subscribe();
    }, 5000);
    return () => {
      clearTimeout(timer);
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  const handlePremiumLayer = (layerName: string, description: string, toggleFn: () => void) => {
    if (!isPremium) {
      toast(`🔒 ${layerName} is Premium`, {
        description,
        action: {
          label: "Upgrade",
          onClick: () => setPricingOpen(true),
        },
      });
      return;
    }
    toggleFn();
  };

  const events = dbEvents && dbEvents.length > 0 ? dbEvents : mockEvents;
  const now = Date.now();
  const timeFiltered = timeFilter === "all"
    ? events
    : events.filter((e) => now - new Date(e.occurred_at).getTime() < timeFilterMs[timeFilter]);
  const filtered = activeFilter === "all"
    ? timeFiltered
    : timeFiltered.filter((e) => e.event_type === activeFilter);
  const totalCount = events.length;

  // Calculate airport risks for map layers
  const airportRisks = useMemo(() => calculateAirportRisks(events), [events]);
  const atRiskAirports = useMemo(
    () => airportRisks.filter((a) => a.status.label !== "OPEN"),
    [airportRisks]
  );

  // Get nearby events for an airport (within 200km)
  const getNearbyEvents = useCallback((airport: AirportRisk) => {
    if (!events?.length) return [];
    return events
      .filter((e) => e.lat && e.lng && haversine(airport.lat, airport.lng, e.lat!, e.lng!) <= 200)
      .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
      .slice(0, 10);
  }, [events]);

  // Push notification when airport transitions to AT RISK or LIKELY CLOSED
  useEffect(() => {
    if (permission !== "granted") return;
    const prev = prevAirportStatusRef.current;

    airportRisks.forEach((airport) => {
      const oldStatus = prev.get(airport.code);
      const newStatus = airport.status.label;
      if (oldStatus && oldStatus === "OPEN" && (newStatus === "AT RISK" || newStatus === "LIKELY CLOSED")) {
        sendNotification(
          `✈️ ${airport.code} — ${newStatus}`,
          {
            body: `${airport.name} (${airport.city}, ${airport.country}) now ${newStatus}. Closure probability: ${airport.closureProb}%. ${airport.count} events nearby.`,
            tag: `airport-${airport.code}`,
            requireInteraction: true,
          }
        );
      }
    });

    // Update ref
    const next = new Map<string, string>();
    airportRisks.forEach((a) => next.set(a.code, a.status.label));
    prevAirportStatusRef.current = next;
  }, [airportRisks, permission, sendNotification]);

  const FilterButtons = () => (
    <div className="flex flex-wrap gap-1.5">
      {(["all", "missile", "drone", "air_raid", "explosion", "movement"] as const).map((type) => (
        <button
          key={type}
          onClick={() => setActiveFilter(type)}
          className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
            activeFilter === type
              ? "border-primary bg-primary/20 text-primary"
              : "border-border bg-secondary text-muted-foreground hover:border-primary/50"
          }`}
        >
          {type === "all" ? "All" : eventLabels[type]}
        </button>
      ))}
    </div>
  );

  const LayerButtons = () => (
    <div className="flex flex-wrap gap-1.5">
      <button
        onClick={() => handlePremiumLayer("US Bases", "See 30+ US installations", () => setShowBases(!showBases))}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          showBases ? "border-blue-500 bg-blue-500/20 text-blue-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}🇺🇸 Bases
      </button>
      <button
        onClick={() => handlePremiumLayer("Fires", "NASA FIRMS active fire data", () => setShowFires(!showFires))}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          showFires ? "border-orange-500 bg-orange-500/20 text-orange-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}🔥 Fires
      </button>
      <button
        onClick={() => handlePremiumLayer("Flights", "OpenSky aircraft tracking", () => setShowFlights(!showFlights))}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          showFlights ? "border-cyan-500 bg-cyan-500/20 text-cyan-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}✈️ Flights
      </button>
      <button
        onClick={() => handlePremiumLayer("Vessels", "AIS vessel tracking", () => setShowVessels(!showVessels))}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          showVessels ? "border-teal-500 bg-teal-500/20 text-teal-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}🚢 Vessels
      </button>
      <button
        onClick={() => toggleInfra("oil")}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          infraLayers.has("oil") ? "border-purple-500 bg-purple-500/20 text-purple-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}🛢️ Oil
      </button>
      <button
        onClick={() => toggleInfra("ports")}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          infraLayers.has("ports") ? "border-sky-500 bg-sky-500/20 text-sky-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}⚓ Ports
      </button>
      <button
        onClick={() => toggleInfra("nuclear")}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          infraLayers.has("nuclear") ? "border-amber-500 bg-amber-500/20 text-amber-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}☢️ Nuclear
      </button>
      <button
        onClick={() => toggleInfra("embassies")}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          infraLayers.has("embassies") ? "border-green-500 bg-green-500/20 text-green-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}🏛️ Embassies
      </button>
      <button
        onClick={() => toggleInfra("cables")}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          infraLayers.has("cables") ? "border-pink-500 bg-pink-500/20 text-pink-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}🔌 Cables
      </button>
      <button
        onClick={() => toggleInfra("closed_airports")}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          infraLayers.has("closed_airports") ? "border-red-500 bg-red-500/20 text-red-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}🛫 Closed Airports{atRiskAirports.length > 0 && ` (${atRiskAirports.length})`}
      </button>
      <button
        onClick={() => toggleInfra("restricted_airspace")}
        className={`rounded-full border px-3 py-1.5 font-mono text-xs font-medium uppercase transition-all ${
          infraLayers.has("restricted_airspace") ? "border-orange-500 bg-orange-500/20 text-orange-400" : "border-border bg-secondary text-muted-foreground"
        }`}
      >
        {!isPremium && <Lock className="mr-1 inline h-3 w-3" />}🚫 Restricted Airspace{atRiskAirports.length > 0 && ` (${atRiskAirports.length})`}
      </button>
    </div>
  );

  const TimeFilterButtons = () => (
    <div className="flex gap-1">
      {TIME_FILTERS.map((tf) => (
        <button
          key={tf.value}
          onClick={() => setTimeFilter(tf.value)}
          className={`rounded-md border px-2 py-1 font-mono text-[10px] font-bold uppercase transition-all ${
            timeFilter === tf.value
              ? "border-primary bg-primary/20 text-primary"
              : "border-border bg-secondary text-muted-foreground"
          }`}
        >
          {tf.label}
        </button>
      ))}
    </div>
  );

  const EventList = () => (
    <div className="space-y-2 max-h-[300px] overflow-y-auto">
      {filtered.filter(e => e.lat && e.lng).slice(0, 20).map((event) => (
        <div key={event.id} className="flex items-center gap-2 rounded-md border border-border bg-background p-2">
          <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: eventColors[event.event_type as EventType] }} />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-foreground truncate">{event.title}</p>
            <p className="text-[10px] text-muted-foreground">{event.city || event.country} • {formatTime(event.occurred_at)}</p>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
    <section id="map" className="border-b border-border">
      <div className={isMobile ? "px-0 py-0" : "container mx-auto px-4 py-8"}>
        {/* Header + Filters */}
        <div className={`${isMobile ? "px-3 pt-3 pb-2" : "mb-4"}`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground">Live Conflict Map</h2>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>{filtered.filter(e => e.lat && e.lng).length} active markers</span>
                {atRiskAirports.length > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-destructive font-mono font-bold animate-pulse">
                    <Plane className="h-3 w-3" />
                    {atRiskAirports.length} airport{atRiskAirports.length !== 1 ? "s" : ""} at risk
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TimeFilterButtons />
              <button
                onClick={locateMe}
                className="rounded-full border border-primary/30 bg-primary/10 p-2 text-primary hover:bg-primary/20 transition-colors"
                title="My Location"
              >
                <Navigation className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!isMobile && (
            <div className="space-y-2">
              <FilterButtons />
              <LayerButtons />
            </div>
          )}

          {isMobile && (
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-3 px-3">
              {(["all", "missile", "drone", "air_raid", "explosion", "movement"] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setActiveFilter(type)}
                  className={`shrink-0 rounded-full border px-3 py-2 font-mono text-xs font-medium uppercase transition-all ${
                    activeFilter === type
                      ? "border-primary bg-primary/20 text-primary"
                      : "border-border bg-secondary text-muted-foreground"
                  }`}
                >
                  {type === "all" ? "All" : eventLabels[type]}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`overflow-hidden ${isMobile ? "" : "rounded-lg border border-border"}`}>
          {isLoading ? (
            <Skeleton className="h-[400px] w-full md:h-[550px]" />
          ) : (
            <MapContainer
              center={[35, 35]}
              zoom={4}
              className={isMobile ? "h-[70vh] w-full" : "h-[400px] w-full md:h-[550px]"}
              zoomControl={!isMobile}
              attributionControl={false}
            >
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
              />


              {/* User location marker */}
              {userLocation && (
                <CircleMarker
                  center={[userLocation.lat, userLocation.lng]}
                  radius={8}
                  pathOptions={{ color: "hsl(142, 70%, 45%)", fillColor: "hsl(142, 70%, 45%)", fillOpacity: 0.6, weight: 3 }}
                >
                  <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                    <div className="text-xs font-bold">📍 Your Location</div>
                  </Tooltip>
                </CircleMarker>
              )}

              {/* US Military Bases */}
              {showBases && US_MILITARY_BASES.map((base, i) => (
                <CircleMarker key={`base-${i}`} center={[base.lat, base.lng]} radius={6}
                  pathOptions={{ color: "#3b82f6", fillColor: "#3b82f6", fillOpacity: 0.6, weight: 2 }}>
                  <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                    <div className="text-xs"><div className="font-bold">{base.name}</div><div className="opacity-80">{base.type} • {base.country}</div></div>
                  </Tooltip>
                </CircleMarker>
              ))}

              {/* Infrastructure: Oil Facilities */}
              {infraLayers.has("oil") && OIL_FACILITIES.map((f, i) => (
                <CircleMarker key={`oil-${i}`} center={[f.lat, f.lng]} radius={5}
                  pathOptions={{ color: infraColors.oil, fillColor: infraColors.oil, fillOpacity: 0.7, weight: 2 }}>
                  <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                    <div className="text-xs"><div className="font-bold">🛢️ {f.name}</div><div className="opacity-80">{f.country}</div></div>
                  </Tooltip>
                </CircleMarker>
              ))}

              {/* Infrastructure: Ports */}
              {infraLayers.has("ports") && MAJOR_PORTS.map((p, i) => (
                <CircleMarker key={`port-${i}`} center={[p.lat, p.lng]} radius={5}
                  pathOptions={{ color: infraColors.ports, fillColor: infraColors.ports, fillOpacity: 0.7, weight: 2 }}>
                  <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                    <div className="text-xs"><div className="font-bold">⚓ {p.name}</div><div className="opacity-80">{p.country}</div></div>
                  </Tooltip>
                </CircleMarker>
              ))}

              {/* Infrastructure: Nuclear */}
              {infraLayers.has("nuclear") && NUCLEAR_FACILITIES.map((n, i) => (
                <CircleMarker key={`nuke-${i}`} center={[n.lat, n.lng]} radius={7}
                  pathOptions={{ color: infraColors.nuclear, fillColor: infraColors.nuclear, fillOpacity: 0.8, weight: 2 }}>
                  <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                    <div className="text-xs"><div className="font-bold">☢️ {n.name}</div><div className="opacity-80">{n.country}</div></div>
                  </Tooltip>
                </CircleMarker>
              ))}

              {/* US Embassies & Consulates */}
              {infraLayers.has("embassies") && US_EMBASSIES.map((e, i) => (
                <CircleMarker key={`emb-${i}`} center={[e.lat, e.lng]} radius={6}
                  pathOptions={{ color: infraColors.embassies, fillColor: infraColors.embassies, fillOpacity: 0.7, weight: 2 }}>
                  <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                    <div className="text-xs"><div className="font-bold">🏛️ {e.name}</div><div className="opacity-80">{e.type} • {e.country}</div></div>
                  </Tooltip>
                </CircleMarker>
              ))}

              {/* Undersea Cables */}
              {infraLayers.has("cables") && UNDERSEA_CABLES.map((cable, ci) => (
                <React.Fragment key={`cable-${ci}`}>
                  <Polyline
                    positions={cable.points.map(p => [p.lat, p.lng] as [number, number])}
                    pathOptions={{ color: infraColors.cables, weight: 2, opacity: 0.6, dashArray: "8 4" }}
                  >
                    <Tooltip direction="top" opacity={0.95}>
                      <div className="text-xs font-bold">🔌 {cable.name}</div>
                    </Tooltip>
                  </Polyline>
                  {cable.points.map((pt, pi) => (
                    <CircleMarker key={`cable-${ci}-${pi}`} center={[pt.lat, pt.lng]} radius={4}
                      pathOptions={{ color: infraColors.cables, fillColor: infraColors.cables, fillOpacity: 0.8, weight: 1 }}>
                      <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
                        <div className="text-xs"><div className="font-bold">🔌 {cable.name}</div><div className="opacity-80">{pt.label}</div></div>
                      </Tooltip>
                    </CircleMarker>
                  ))}
                </React.Fragment>
              ))}

              {/* Closed Airports — with detail panel on click */}
              {infraLayers.has("closed_airports") && atRiskAirports.map((airport) => {
                const markerColor = airport.status.label === "LIKELY CLOSED" ? "#ef4444" : "#f97316";
                const nearbyEvents = getNearbyEvents(airport);
                return (
                  <CircleMarker key={`apt-${airport.code}`} center={[airport.lat, airport.lng]} radius={8}
                    pathOptions={{ color: markerColor, fillColor: markerColor, fillOpacity: 0.8, weight: 2 }}
                    eventHandlers={{ click: () => {} }}
                  >
                    <Popup maxWidth={320} minWidth={260}>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-sm">🛫 {airport.code} — {airport.name}</span>
                        </div>
                        <div className="text-xs opacity-80">{airport.city}, {airport.country}</div>
                        <div className="flex items-center gap-2">
                          <span className="inline-block rounded px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: markerColor }}>
                            {airport.status.label}
                          </span>
                          <span className="text-[10px] opacity-70">Closure prob: {airport.closureProb}%</span>
                        </div>
                        <div className="text-[10px] opacity-60">Risk: {airport.risk}/5 • {airport.count} events within 200km</div>
                        
                        {nearbyEvents.length > 0 && (
                          <div className="border-t border-gray-200 pt-2 mt-1">
                            <div className="font-bold text-[11px] mb-1">📋 Contributing Events</div>
                            <div className="space-y-1 max-h-[150px] overflow-y-auto">
                              {nearbyEvents.map((evt) => (
                                <div key={evt.id} className="flex items-start gap-1.5 text-[10px]">
                                  <span className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: eventColors[evt.event_type as EventType] }} />
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium leading-tight truncate">{evt.title}</div>
                                    <div className="opacity-60">{evt.city || evt.country} • {formatTime(evt.occurred_at)} • {Math.round(haversine(airport.lat, airport.lng, evt.lat!, evt.lng!))}km</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}

              {/* Restricted Airspace Zones */}
              {infraLayers.has("restricted_airspace") && atRiskAirports.map((airport) => {
                const zoneColor = airport.status.label === "LIKELY CLOSED" ? "#ef4444" : "#f97316";
                return (
                  <Circle key={`airspace-${airport.code}`}
                    center={[airport.lat, airport.lng]}
                    radius={200000}
                    pathOptions={{
                      color: zoneColor,
                      fillColor: zoneColor,
                      fillOpacity: 0.08,
                      weight: 1,
                      dashArray: "6 4",
                    }}
                  >
                    <Tooltip direction="center" opacity={0.95}>
                      <div className="text-xs">
                        <div className="font-bold">🚫 Restricted Airspace — {airport.code}</div>
                        <div style={{ color: zoneColor }} className="font-bold">{airport.status.label}</div>
                        <div className="opacity-60">~200km radius • {airport.closureProb}% closure probability</div>
                      </div>
                    </Tooltip>
                  </Circle>
                );
              })}

              {showFires && fires.map((fire, i) => (
                <CircleMarker key={`fire-${i}`} center={[fire.latitude, fire.longitude]}
                  radius={Math.max(3, Math.min(8, fire.frp / 50))}
                  pathOptions={{ color: "#f97316", fillColor: fire.frp > 100 ? "#ef4444" : "#f97316", fillOpacity: 0.7, weight: 1 }}>
                  <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                    <div className="text-xs"><div className="font-bold">🔥 Active Fire</div><div className="opacity-80">FRP: {fire.frp.toFixed(1)} MW • {fire.confidence}</div></div>
                  </Tooltip>
                </CircleMarker>
              ))}

              {/* Aircraft */}
              {showFlights && aircraft.map((ac, i) => (
                <CircleMarker key={`ac-${ac.icao24}-${i}`} center={[ac.latitude, ac.longitude]}
                  radius={ac.is_military ? 5 : 3}
                  pathOptions={{ color: ac.is_military ? "#ef4444" : "#06b6d4", fillColor: ac.is_military ? "#ef4444" : "#06b6d4", fillOpacity: ac.is_military ? 0.8 : 0.5, weight: ac.is_military ? 2 : 1 }}>
                  <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                    <div className="text-xs"><div className="font-bold">{ac.is_military ? "🎖️ Military" : "✈️ Civil"} — {ac.callsign || ac.icao24}</div><div className="opacity-80">{ac.origin_country}</div></div>
                  </Tooltip>
                </CircleMarker>
              ))}

              {/* Vessels */}
              {showVessels && vessels.map((v, i) => (
                <CircleMarker key={`vessel-${v.mmsi}-${i}`} center={[v.latitude, v.longitude]}
                  radius={v.is_military ? 5 : 3}
                  pathOptions={{ color: v.is_military ? "#dc2626" : "#14b8a6", fillColor: v.is_military ? "#dc2626" : "#14b8a6", fillOpacity: v.is_military ? 0.8 : 0.5, weight: v.is_military ? 2 : 1 }}>
                  <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                    <div className="text-xs"><div className="font-bold">{v.is_military ? "⚓ Military" : "🚢 Vessel"} — {v.name || v.mmsi}</div></div>
                  </Tooltip>
                </CircleMarker>
              ))}

              <MarkerClusterGroup
                iconCreateFunction={createClusterIcon}
                maxClusterRadius={40}
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
                zoomToBoundsOnClick={true}
                disableClusteringAtZoom={10}
              >
                {filtered
                  .filter((e) => e.lat != null && e.lng != null)
                  .map((event) => (
                    <Marker
                      key={event.id}
                      position={[event.lat!, event.lng!]}
                      icon={createMarkerIcon(event.event_type as EventType)}
                    >
                      <Popup>
                        <div className="min-w-[200px] space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="inline-block h-2 w-2 rounded-full" style={{ background: eventColors[event.event_type as EventType] }} />
                            <span className="font-mono text-[10px] font-medium uppercase" style={{ color: eventColors[event.event_type as EventType] }}>
                              {eventLabels[event.event_type as EventType]}
                            </span>
                          </div>
                          <h3 className="text-sm font-bold leading-tight">{event.title}</h3>
                          {event.summary && <p className="text-xs leading-relaxed opacity-80">{event.summary}</p>}
                          <div className="flex items-center justify-between border-t border-border pt-2">
                            <span className="font-mono text-[10px] opacity-60">{formatTime(event.occurred_at)}</span>
                            <span className="text-[10px] font-medium">{event.source_name}</span>
                          </div>
                          {event.confidence && (
                            <div className="flex items-center gap-2">
                              <div className="h-1 flex-1 rounded-full bg-secondary">
                                <div className="h-1 rounded-full" style={{ width: `${event.confidence}%`, background: event.confidence > 70 ? 'hsl(142, 70%, 45%)' : event.confidence > 40 ? 'hsl(38, 92%, 50%)' : 'hsl(0, 84%, 60%)' }} />
                              </div>
                              <span className="font-mono text-[9px] text-muted-foreground">{event.confidence}%</span>
                            </div>
                          )}
                          {event.source_url && event.source_url !== "#" && (
                            <a href={event.source_url} target="_blank" rel="noopener noreferrer" className="block text-center text-xs font-medium text-primary hover:underline">View Source →</a>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
              </MarkerClusterGroup>

              {/* Free-tier overlay */}
              {!isPremium && totalCount >= 20 && (
                <div className="absolute inset-x-0 bottom-0 z-[1000] bg-gradient-to-t from-background/90 via-background/60 to-transparent p-6 text-center">
                  <p className="mb-2 text-sm font-semibold text-foreground">
                    Showing 20 of {totalCount}+ events (last 24h only)
                  </p>
                  <Button
                    size="sm"
                    className="gap-1.5 font-semibold"
                    onClick={() => setPricingOpen(true)}
                  >
                    <Zap className="h-3.5 w-3.5" /> Upgrade to see all events
                  </Button>
                </div>
              )}
            </MapContainer>
          )}
        </div>

        {/* Mobile bottom sheet drawer for events & layers */}
        {isMobile && (
          <Drawer.Root>
            <Drawer.Trigger asChild>
              <button className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[1100] flex items-center gap-2 rounded-full border border-primary/40 bg-card/95 backdrop-blur-md px-5 py-3 shadow-xl shadow-primary/10 active:scale-95 transition-transform">
                <Filter className="h-4 w-4 text-primary" />
                <span className="text-sm font-bold text-foreground">{filtered.filter(e => e.lat && e.lng).length} Events</span>
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              </button>
            </Drawer.Trigger>
            <Drawer.Portal>
              <Drawer.Overlay className="fixed inset-0 z-[1200] bg-black/60" />
              <Drawer.Content className="fixed inset-x-0 bottom-0 z-[1300] flex max-h-[85vh] flex-col rounded-t-2xl border-t border-border bg-card">
                <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted-foreground/30" />
                <div className="overflow-y-auto px-4 pb-6 pt-3 space-y-4">
                  {/* Layer toggles */}
                  <div>
                    <h3 className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Layers</h3>
                    <div className="flex flex-wrap gap-1.5">
                      <LayerButtons />
                    </div>
                  </div>
                  {/* Event list */}
                  <div>
                    <h3 className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                      Recent Events ({filtered.filter(e => e.lat && e.lng).length})
                    </h3>
                    <div className="space-y-1.5">
                      {filtered.filter(e => e.lat && e.lng).slice(0, 30).map((event) => (
                        <div key={event.id} className="flex items-start gap-2.5 rounded-lg border border-border bg-background p-2.5 active:bg-muted transition-colors">
                          <span className="mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: eventColors[event.event_type as EventType] }} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground leading-tight">{event.title}</p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {event.city || event.country} • {formatTime(event.occurred_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Drawer.Content>
            </Drawer.Portal>
          </Drawer.Root>
        )}

        {/* Desktop Legend */}
        {!isMobile && (
          <div className="mt-3 flex flex-wrap items-center gap-4">
            {(Object.keys(eventColors) as EventType[]).map((type) => {
              const color = eventColors[type];
              return (
                <div key={type} className="flex items-center gap-1.5">
                  <span
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full"
                    style={{ background: `${color}22`, border: `1.5px solid ${color}`, color, boxShadow: `0 0 6px ${color}66` }}
                    dangerouslySetInnerHTML={{ __html: eventIcons[type] }}
                  />
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">{eventLabels[type]}</span>
                </div>
              );
            })}
            {showBases && <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#3b82f6" }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">US Base</span></div>}
            {infraLayers.has("oil") && <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: infraColors.oil }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">Oil Facility</span></div>}
            {infraLayers.has("ports") && <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: infraColors.ports }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">Port</span></div>}
            {infraLayers.has("nuclear") && <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: infraColors.nuclear }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">Nuclear</span></div>}
            {infraLayers.has("embassies") && <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: infraColors.embassies }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">US Embassy</span></div>}
            {infraLayers.has("cables") && <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: infraColors.cables }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">Undersea Cable</span></div>}
            {infraLayers.has("closed_airports") && <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: infraColors.closed_airports }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">Closed Airport ({atRiskAirports.length})</span></div>}
            {infraLayers.has("restricted_airspace") && <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full border border-orange-500" style={{ background: `${infraColors.restricted_airspace}33` }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">Restricted Airspace</span></div>}
            {showFlights && (
              <>
                <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#ef4444" }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">Military ({aircraft.filter(a => a.is_military).length})</span></div>
                <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#06b6d4" }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">Civil ({aircraft.filter(a => !a.is_military).length})</span></div>
              </>
            )}
            {showVessels && (
              <>
                <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#dc2626" }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">Military Vessel</span></div>
                <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#14b8a6" }} /><span className="font-mono text-[10px] uppercase text-muted-foreground">Civil Vessel</span></div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
    <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </>
  );
};

export default ConflictMap;
